'use strict';

import { tracker } from '@openreplay/tracker'
import trackerAssist from '@openreplay/tracker-assist';
tracker.configure({
  projectKey: "vdRQHBSHg3Kld1Ol85Kq",
  inlineCss: 3, // InlineCssMode.PlainFetched
  connAttemptCount: 9999999,
  obscureTextEmails: false,
  obscureInputEmails: false,
  obscureInputNumbers: false,
  defaultInputMode: 0, // InputMode.Plain
  forceSingleTab: true,
  consoleThrottling: 1000,
});
const assistOptions = {};
tracker.use(trackerAssist(assistOptions));
// OpenReplay rejects with "Browser doesn't support required api, or doNotTrack is active."
// when its env check fails. Without .catch() the rejection bubbles to window.onunhandledrejection
// and Sentry captures it as an unhandled exception — pure noise, since OpenReplay is passive
// telemetry and the app continues normally either way.
tracker.start().catch((reason) => {
  console.info('OpenReplay: tracker did not start:', reason);
});


import * as Sentry from "@sentry/browser";

window.Sentry = Sentry;
window.SENTRY_DSN = "https://a2dfbd2e68b1be4279c3e11c0dbe60e1@o4508137409609728.ingest.de.sentry.io/4508142697250896";

// Determine environment based on hostname
const determineEnvironment = () => {
    const hostname = window.location.hostname;

    if (hostname === 'ib-dev-pwa.dev.kranki.net') {
        return 'dev';
    }

    const devMatch = hostname.match(/^.*\.dev(\d)-ibeads\.net$/);
    if (devMatch) {
        return `dev${devMatch[1]}`;
    }

    if (hostname === 'pwa.uat-ibeads.net') {
        return 'uat';
    }

    if (hostname.endsWith('.ibo.org')) {
        return 'production';
    }

    return 'dev-unknown'; // fallback environment
};

const MAX_REPLAY_DURATION = 3600000; // 1 hour in milliseconds
const REPLAY_CHECK_INTERVAL = 30000; // 30 seconds
const REPLAY_FLUSH_INTERVAL = 300000; // 5 minutes
// Coalesce bursts of lifecycle/error flushes; terminal events bypass this.
const FLUSH_MIN_INTERVAL_MS = 2000;
let replaySessionStartTime = null;
let replayContinuationTimer = null;
// Updated after a successful flush() returns. Drives the 5-min periodic
// cadence in checkReplayContinuation. Also seeded at session start so the
// first periodic flush fires 5 min after start, not immediately.
let lastFlushTime = null;
// Updated *before* awaiting flush() and used solely as the throttle gate.
// Kept separate from lastFlushTime so session-start seeding doesn't suppress
// early flushes, and so concurrent triggers within the throttle window are
// dedup'd before they can race past each other.
let lastFlushAttemptTime = null;
let consecutiveStartFailures = 0;
let isContinuing = false;

// Single flush helper used by periodic, lifecycle, and error-driven triggers.
// `bypassThrottle: true` for terminal events (pagehide/freeze/SW reload) where
// missing the flush means losing the tail of the replay entirely.
const flushReplayNow = async (reason, { bypassThrottle = false } = {}) => {
  try {
    const replay = Sentry.getReplay();
    if (!replay) return;

    const now = Date.now();
    if (!bypassThrottle && lastFlushAttemptTime && (now - lastFlushAttemptTime) < FLUSH_MIN_INTERVAL_MS) {
      return;
    }
    // Mark before await so concurrent triggers in the throttle window early-return.
    lastFlushAttemptTime = now;

    // The replay ID can be transiently null during the async start window or
    // mid-continuation. We still call flush() — it's a no-op if there's
    // nothing to send, and skipping it on a terminal event would lose the tail.
    const replayId = replay.getReplayId();
    await replay.flush();
    lastFlushTime = Date.now();
    console.debug(`Sentry replay: Flushed (reason: ${reason}, id: ${replayId || 'unknown'})`);
  } catch (error) {
    console.warn(`Sentry replay: Flush failed (reason: ${reason}):`, error);
  }
};

// Try to start replay and verify it actually started
// Uses a short delay to allow the SDK to set the replay ID asynchronously
const tryStartReplay = async (replay, reason) => {
  try {
    replay.start();

    // Wait briefly for the SDK to assign a replay ID (may be async)
    let newId = replay.getReplayId();
    if (!newId) {
      await new Promise(resolve => setTimeout(resolve, 100));
      newId = replay.getReplayId();
    }

    if (newId) {
      consecutiveStartFailures = 0;
      replaySessionStartTime = Date.now();
      lastFlushTime = Date.now();
      console.log(`Sentry replay: Started (reason: ${reason}, id: ${newId})`);
      return true;
    } else {
      consecutiveStartFailures++;
      console.warn(`Sentry replay: start() produced no replay ID (reason: ${reason}, consecutive failures: ${consecutiveStartFailures})`);
      return false;
    }
  } catch (error) {
    consecutiveStartFailures++;
    console.error(`Sentry replay: start() threw (reason: ${reason}, consecutive failures: ${consecutiveStartFailures}):`, error);
    return false;
  }
};

// Function to start a new replay session continuation (stop current, start new)
// Guarded by isContinuing flag to prevent overlapping stop/start operations
const startReplayContinuation = async (replay, reason = 'max duration') => {
  if (isContinuing) {
    console.debug('Sentry replay: Continuation already in progress, skipping');
    return;
  }
  isContinuing = true;
  const previousReplayId = replay.getReplayId();
  let newReplayStarted = false;
  try {
    console.log(`Sentry replay: Stopping current session (reason: ${reason}, previous id: ${previousReplayId})`);
    await replay.stop();
    newReplayStarted = await tryStartReplay(replay, `continuation after ${reason}`);
  } catch (error) {
    console.error('Sentry replay: Error during continuation:', error);
    // stop() failed — try starting anyway
    newReplayStarted = await tryStartReplay(replay, `continuation after ${reason} (stop failed)`);
  } finally {
    isContinuing = false;
  }
  if (previousReplayId && newReplayStarted) {
    Sentry.setTag('previous_replay_id', previousReplayId);
    tracker.setMetadata('previous_replay_id', previousReplayId);
    console.log(`Sentry replay: Tagged new session with previous_replay_id: ${previousReplayId}`);
  }
};

// Main check function — runs every 30 seconds
const checkReplayContinuation = async () => {
  try {
    const replay = Sentry.getReplay();
    if (!replay) {
      console.debug('Sentry replay: Check skipped - replay integration not available');
      return;
    }

    const now = Date.now();
    const replayId = replay.getReplayId();

    // If replay is not running, restart it
    if (!replayId) {
      console.log('Sentry replay: Not running, attempting restart...');
      await tryStartReplay(replay, 'detected stopped');
      return;
    }

    // Initialize tracking if needed
    if (!replaySessionStartTime) {
      replaySessionStartTime = now;
      lastFlushTime = now;
    }

    const elapsed = now - replaySessionStartTime;
    const elapsedMinutes = Math.round(elapsed / 60000);

    // If max duration reached, do continuation (stop + start new session)
    if (elapsed >= MAX_REPLAY_DURATION) {
      console.log(`Sentry replay: Max duration reached (${elapsedMinutes}min), starting continuation...`);
      await startReplayContinuation(replay, 'max duration');
      return;
    }

    // Periodic flush to force-send buffered data
    if (lastFlushTime && (now - lastFlushTime) >= REPLAY_FLUSH_INTERVAL) {
      await flushReplayNow(`periodic ${elapsedMinutes}min`);
    }
  } catch (error) {
    console.error('Sentry replay: Error in check:', error);
  }
};

const detectedEnvironment = determineEnvironment();

// Device ID generation - readable format with two words + alphanumeric suffix
const DEVICE_ID_WORDS = [
  'ocean', 'mountain', 'forest', 'river', 'valley',
  'sunset', 'sunrise', 'thunder', 'lightning', 'rainbow',
  'eagle', 'falcon', 'dolphin', 'tiger', 'panther',
  'crystal', 'diamond', 'emerald', 'sapphire', 'ruby'
];

const DEVICE_ID_COOKIE_NAME = 'ib_device_id';
const DEVICE_ID_COOKIE_MAX_AGE = 365 * 24 * 60 * 60; // 1 year in seconds

/**
 * Generates a readable device ID in the format: word1-word2-xxxx
 * where xxxx is 4 alphanumeric characters
 */
const generateDeviceId = () => {
  // Pick two random words
  const word1 = DEVICE_ID_WORDS[Math.floor(Math.random() * DEVICE_ID_WORDS.length)];
  const word2 = DEVICE_ID_WORDS[Math.floor(Math.random() * DEVICE_ID_WORDS.length)];
  
  // Generate 4-character alphanumeric suffix
  const chars = '0123456789abcdefghijklmnopqrstuvwxyz';
  let suffix = '';
  for (let i = 0; i < 4; i++) {
    suffix += chars[Math.floor(Math.random() * chars.length)];
  }
  
  return `${word1}-${word2}-${suffix}`;
};

/**
 * Gets or creates a device ID cookie
 * Returns the device ID string
 */
const getOrCreateDeviceId = () => {
  // Try to read existing cookie
  const cookies = document.cookie.split(';');
  for (let cookie of cookies) {
    const [name, value] = cookie.trim().split('=');
    if (name === DEVICE_ID_COOKIE_NAME && value) {
      console.log(`Device ID: Found existing device ID: ${value}`);
      return value;
    }
  }
  
  // No existing cookie, generate new device ID
  const deviceId = generateDeviceId();
  
  // Set permanent cookie (1 year expiration)
  const expirationDate = new Date();
  expirationDate.setTime(expirationDate.getTime() + (DEVICE_ID_COOKIE_MAX_AGE * 1000));
  const cookieValue = `${DEVICE_ID_COOKIE_NAME}=${deviceId}; expires=${expirationDate.toUTCString()}; path=/; SameSite=Lax`;
  document.cookie = cookieValue;
  
  console.log(`Device ID: Generated new device ID: ${deviceId}`);
  return deviceId;
};

// Get or create device ID and set as Sentry tag immediately
const deviceId = getOrCreateDeviceId();
Sentry.setTag('device_id', deviceId);
console.log(`Device ID: Set Sentry tag 'device_id' to: ${deviceId}`);
tracker.setMetadata('device_id', deviceId);

// Read school_code BEFORE Sentry.init so it lands in initialScope. The replay session
// is created inside init() and pins its session-level tags from the scope at that
// moment — any setTag after init updates the scope for future error events but does
// not retroactively update an already-started replay's indexed tags. To repair that
// for in-flight sessions we trigger a replay continuation (stop+start) once the real
// school code becomes known; see ensureReplayTagsCurrent / updateSentrySchoolCode.
let bootSchoolCode = '-1';
try {
  const stored = sessionStorage.getItem('sentrySchoolCode');
  if (stored && /^\d+$/.test(stored)) {
    bootSchoolCode = stored;
  }
} catch (_) { /* sessionStorage unavailable */ }

// Tracks the school code currently pinned into the active replay session's scope.
// Diverges from the live tag whenever updateSentrySchoolCode fires for a new value
// while a replay is already running; ensureReplayTagsCurrent reconciles by
// continuation.
let currentReplaySchoolCode = bootSchoolCode;

// Initialize Sentry
Sentry.init({
  dsn: window.SENTRY_DSN,
  environment: detectedEnvironment,
  release: __BUILD_NO__,
  initialScope: {
    tags: {
      device_id: deviceId,
      school_code: bootSchoolCode
    }
  },
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.feedbackIntegration({
        colorScheme: "system",
      }),
    Sentry.replayIntegration({
      networkDetailAllowUrls: [window.location.origin],
      networkRequestHeaders: ["Cache-Control"],
      networkCaptureBodies: true,
      maskAllText: false,
      maskAllInputs: false,
      blockAllMedia: false,
      stickySession: false,
      maxReplayDuration: MAX_REPLAY_DURATION,
    }),
  ],
  // Tracing
  tracesSampleRate: 1.0,
  // Session Replay
  replaysSessionSampleRate: 1.0,
  replaysOnErrorSampleRate: 1.0,
  tracePropagationTargets: [
    "localhost",
    /\.dev\.kranki\.net$/,
    /\.uat-ibeads\.net$/,
    /\.ibo\.org$/,
    /\.sit-ibeads\.net$/,
    /\.dev\d-ibeads\.net$/
  ],
  // Flush replay buffer after errors so the moments leading up to the failure
  // are persisted even if the user closes/crashes the app immediately after.
  // beforeSend fires for all non-transaction events including captureMessage,
  // so guard to actual exceptions / error+fatal levels — we don't want every
  // info/warning message to schedule a flush.
  // Scheduled via setTimeout so the error event itself ships first.
  beforeSend(event) {
    const isErrorEvent =
      !!event.exception ||
      event.level === 'error' ||
      event.level === 'fatal';
    if (isErrorEvent) {
      setTimeout(() => flushReplayNow('beforeSend:error'), 0);
    }
    return event;
  },
});

// Ensure device ID tag is set after Sentry initialization (in case init clears tags)
Sentry.setTag('device_id', deviceId);
tracker.setMetadata('device_id', deviceId);
tracker.setUserAnonymousID(deviceId);

// Mirror the boot value into OpenReplay metadata. The Sentry tag is already set via
// initialScope above; if no stored value was found, fall back to the async dev
// override / managed config lookup below.
tracker.setMetadata('school_code', bootSchoolCode);
if (bootSchoolCode !== '-1') {
  console.log('Sentry: school_code restored from sessionStorage:', bootSchoolCode);
} else {
  (async () => {
    try {
      const isDevMode = typeof __BUILD_NO__ !== 'undefined' && __BUILD_NO__ === 'dev-local';
      const devOverride = isDevMode && localStorage.getItem('devSchoolCode');
      if (devOverride && /^\d+$/.test(String(devOverride))) {
        updateSentrySchoolCode(String(devOverride));
        console.log('Sentry: school_code set from dev override:', devOverride);
        return;
      }
      if (typeof navigator.managed?.getManagedConfiguration === 'function') {
        const cfg = await navigator.managed.getManagedConfiguration(['primarySchoolCode']);
        const code = cfg && cfg.primarySchoolCode != null ? String(cfg.primarySchoolCode) : null;
        if (code && /^\d+$/.test(code)) {
          updateSentrySchoolCode(code);
          console.log('Sentry: school_code set from managed config');
          return;
        }
      }
      console.log('Sentry: school_code defaulting to -1 (no managed config available)');
    } catch (err) {
      console.warn('Sentry: Error reading managed config for school_code:', err);
    }
  })();
}

// Set up periodic check for replay continuation
// Wait a bit for Sentry to initialize, then start checking
setTimeout(async () => {
  const replay = Sentry.getReplay();
  if (!replay) {
    console.warn('Sentry replay: Replay integration not available during initialization');
    return;
  }

  const replayId = replay.getReplayId();
  console.log(`Sentry replay: Initializing monitoring (check: ${REPLAY_CHECK_INTERVAL / 1000}s, flush: ${REPLAY_FLUSH_INTERVAL / 1000}s, max: ${MAX_REPLAY_DURATION / 60000}min), current id: ${replayId}`);

  if (replayId) {
    // Already running — initialize tracking
    replaySessionStartTime = Date.now();
    lastFlushTime = Date.now();
    console.log(`Sentry replay: Already running (id: ${replayId})`);
  } else {
    // Not running — start it
    await tryStartReplay(replay, 'initialization');
  }

  // Run check immediately, then every 30 seconds
  await checkReplayContinuation();
  replayContinuationTimer = setInterval(checkReplayContinuation, REPLAY_CHECK_INTERVAL);
}, 1000);

// Lifecycle-driven flushes. The Sentry SDK installs its own pagehide/visibility
// flush via sendBeacon, but beacons are silently dropped at >64 KB — and our
// network-detail capture means buffers regularly exceed that. These extra
// hooks call replay.flush() through the regular fetch path, which can succeed
// where the beacon does not, and (for visibilitychange) typically runs while
// the page still has time to complete the request.
const wireReplayLifecycleFlushes = () => {
  // Most reliable lifecycle signal across mobile/desktop/PWA: fires when the
  // tab is backgrounded, the PWA is minimized, or the browser is closing.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      flushReplayNow('visibilitychange:hidden', { bypassThrottle: true });
    }
  });

  // BFCache + true unload. Last chance before the page goes away.
  window.addEventListener('pagehide', () => {
    flushReplayNow('pagehide', { bypassThrottle: true });
  });

  // Page Lifecycle API: Chromium is about to freeze the tab to reclaim memory.
  document.addEventListener('freeze', () => {
    flushReplayNow('freeze', { bypassThrottle: true });
  });

  // A new service worker took control — a hard reload usually follows within
  // a few hundred ms, so flush whatever is buffered before we lose it.
  if (navigator.serviceWorker) {
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      flushReplayNow('sw:controllerchange', { bypassThrottle: true });
    });
  }
};
wireReplayLifecycleFlushes();

// Add global error event listener to capture unhandled errors
window.addEventListener('error', (error) => {
  Sentry.captureException(error);
});

// Add unhandled promise rejection listener
window.addEventListener('unhandledrejection', (event) => {
  Sentry.captureException(event.reason);
});

export function updateSentryUsername(username, specimenName) {
  try {
    const displayName = specimenName
      ? `${username} (Specimen: ${specimenName})`
      : username;
    const currentUser = Sentry.getIsolationScope().getUser() || {};
    Sentry.setUser({ ...currentUser, username: displayName });
    tracker.setMetadata('username', displayName);
  } catch (e) {
    console.warn('Sentry: Failed to update username:', e);
  }
}

// Force the active replay session to inherit the latest scope tags. The Sentry SDK
// pins replay tags at session start, so a setTag after start does not relabel an
// in-flight replay. Stop+start produces a new replay session that picks up the
// current scope; the previous segment is preserved in Sentry under its own ID and
// linked via the previous_replay_id tag set inside startReplayContinuation.
const ensureReplayTagsCurrent = async (reason) => {
  try {
    const replay = Sentry.getReplay();
    if (!replay) return;
    if (!replay.getReplayId()) {
      // No replay running yet — when one starts it will inherit the current scope.
      return;
    }
    await startReplayContinuation(replay, reason);
  } catch (e) {
    console.warn('Sentry: Failed to relabel replay session:', e);
  }
};

export function updateSentrySchoolCode(schoolCode) {
  try {
    const code = (schoolCode != null && schoolCode !== '') ? String(schoolCode) : '-1';
    Sentry.setTag('school_code', code);
    tracker.setMetadata('school_code', code);
    if (code !== '-1') {
      sessionStorage.setItem('sentrySchoolCode', code);
    } else {
      sessionStorage.removeItem('sentrySchoolCode');
    }
    // Only restart on a transition into a real, different code. Going back to '-1'
    // (logout) keeps the current replay so we don't lose the in-progress segment
    // for an unrelated reason.
    if (code !== '-1' && code !== currentReplaySchoolCode) {
      const previous = currentReplaySchoolCode;
      currentReplaySchoolCode = code;
      ensureReplayTagsCurrent(`school_code:${previous}->${code}`);
    } else if (code === '-1') {
      currentReplaySchoolCode = '-1';
    }
  } catch (e) {
    console.warn('Sentry: Failed to update school_code:', e);
  }
}

export function configureSentryAngularJsHandler(app) {
  app
    .config(['$provide', function($provide) {
      $provide.decorator('$exceptionHandler', ['$delegate', function($delegate) {
        return function(exception, cause) {
          // Capture the error in Sentry
          Sentry.withScope(function(scope) {
            if (cause) {
              scope.setExtra('cause', cause);
            }

            // Handle AngularJS specific error formatting
            const angularPattern = /^\[((?:[$a-zA-Z0-9]+:)?(?:[$a-zA-Z0-9]+))\] (.*?)\n?(\S+)$/;
            const matches = angularPattern.exec(exception.message || '');

            if (matches) {
              scope.setExtra('angularType', matches[1]);
              scope.setExtra('angularDocs', matches[3].substr(0, 250));

              // Reformat the error for better readability in Sentry
              scope.setExtra('originalMessage', exception.message);
              exception.message = `${matches[1]}: ${matches[2]}`;
            }

            Sentry.captureException(exception);
          });

          // Call the original $exceptionHandler
          $delegate(exception, cause);
        };
      }]);
    }]);
}

let lastDetection = null;
const detectConsoleBySize = () => {
  if (!detectedEnvironment.startsWith('dev')) {
    return false;
  }
  const threshold = 150; // Arbitrary value to detect size changes
  if (!lastDetection || Date.now() - lastDetection > 2000) {
    lastDetection = Date.now();
    const isOpen = (window.outerWidth - window.innerWidth > threshold) || (window.outerHeight - window.innerHeight > threshold);
    //console.log("detectConsoleBySize", window.outerWidth, window.innerWidth, window.outerHeight, window.innerHeight, isOpen);
    return isOpen;
  } else {
    return lastDetection;
  }
};



export function configureServiceWorkerLogForwardingToSentry() {
  navigator.serviceWorker.addEventListener('message', event => {
    if (event.data && event.data.type === 'SW_LOG') {
      if (detectConsoleBySize()) {
        return;
      }
      if (window.skipSwLogMessages === true) {
        return;
      }

      const { level, message, fileName, lineNumber, columnNumber, timestamp } = event.data;

      // Use eval to preserve source information in console logs
      const logStatement = `console[level].call(console, "[SW LOG] " + message)`;
      const sourceURL = `\n//# sourceURL=${fileName}:${lineNumber}:${columnNumber}`;
      eval(logStatement + sourceURL);

      if (event.data.error) {
        Sentry.captureException(event.data.error, {
          tags: { fileName, lineNumber, columnNumber },
          extra: { timestamp }
        });
        if (event.data.error instanceof Error) {
          tracker.handleError(event.data.error, {
            source: { filename: fileName, lineno: lineNumber, colno: columnNumber },
          });
        } else {
          tracker.handleError(new Error(event.data.error), {
            source: { filename: fileName, lineno: lineNumber, colno: columnNumber },
          });
        }
      }
      /*
      // Handle errors as exceptions
      if (level === 'error') {
        const error = new Error(message);
        error.stack = `at ${fileName}:${lineNumber}:${columnNumber}`;
        Sentry.captureException(error, {
          tags: { fileName, lineNumber, columnNumber },
          extra: { timestamp }
        });
      } else {
        // Non-error logs go as messages
        Sentry.captureMessage(message, {
          level,
          tags: { fileName, lineNumber, columnNumber },
          extra: { timestamp }
        });
      }
      */
    }
  });
}
